import React from 'react';
import { useSelector } from 'react-redux';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import { HeadingSBold } from 'components/Typography/Typography';
import classNames from 'classnames';
import CustomButton from 'components/CustomButtons/Button';
import { makeStyles } from '@material-ui/core';
import InfoRow from 'components/Info/InfoRow';
import styles from 'assets/jss/material-dashboard-pro-react/components/infoStyles';
import CanPerform from 'components/CanPerform/CanPerform';
import { hasPermission } from 'utils/permissions/permissions';
import { userTypeSelector } from 'redux/modules/session/selectors';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IInfoCardButton {
	label: string;
	onClick: () => void;
	id?: string;
}

interface IInfoCardProps {
	title?: string;
	header?: string | React.ReactElement;
	elements: types.users.IPermission[];
	buttons?: IInfoCardButton[];
	action?: string;
	topButton?: IInfoCardButton;
	emptyElementsComponent?: React.ReactElement | string;
	rowsHeader?: string[];
	noBorder?: boolean;
	body?: React.ReactElement | string;
}

const InfoCard = ({
	title,
	elements,
	header,
	buttons = [],
	topButton,
	emptyElementsComponent,
	rowsHeader,
	noBorder,
	action,
	body,
}: IInfoCardProps) => {
	const classes = useStyles();
	const userType = useSelector(userTypeSelector);

	const elem = (
		<ConditionalRender
			show={elements && elements.length > 0}
			onTrue={() => (
				<>
					{rowsHeader && <InfoRow title={rowsHeader[0]} value={rowsHeader[1]} isHeader />}
					{elements.map((element, ind) => (
						<InfoRow {...element} key={ind} noBorder={noBorder} />
					))}
					{action && (
						<CanPerform
							action={action}
							yes={
								<ConditionalRender
									show={buttons.length > 0}
									onTrue={() => (
										<div className={classes.infoCardButtonsContainer}>
											{buttons.map(({ label, onClick, id, ...props }) => (
												<CustomButton
													onClick={onClick}
													{...props}
													key={label}
													color="tertiary-light"
													id={id}
												>
													{label}
												</CustomButton>
											))}
										</div>
									)}
								/>
							}
						/>
					)}
				</>
			)}
		/>
	);
	return (
		<Card
			className={classNames(classes.infoCard, {
				[classes.withButtons]: buttons.length > 0 && hasPermission(userType, action || 'default'),
				[classes.withBody]: body,
			})}
		>
			<CardBody>
				<>
					{header}
					{title && (
						<div className={classes.title}>
							<HeadingSBold disableMargin>{title}</HeadingSBold>
						</div>
					)}
					<ConditionalRender
						show={!!topButton}
						onTrue={() => (
							<CustomButton
								className={classes.topButton}
								onClick={topButton?.onClick}
								color="tertiary-light"
							>
								{topButton ? topButton?.label : ''}
							</CustomButton>
						)}
					/>

					{elem}
					<ConditionalRender
						show={elements.length === 0}
						onTrue={() => emptyElementsComponent as React.ReactElement}
					/>
					{body}
				</>
			</CardBody>
		</Card>
	);
};

export default InfoCard;
